import { FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createEnterprise,
  Enterprise,
  InviteCreateResp,
  inviteEnterpriseAdmin,
  listEnterprises,
} from "../../lib/api";
import { Card, ErrorNote, Loading, PageTitle } from "../../components/admin/ui";
import { InviteResult } from "../../components/admin/InviteResult";
import { ResetPasswordForm } from "../../components/admin/ResetPasswordForm";

/** Super-admin root: directory of every enterprise, a create form,
 * and a per-enterprise "invite first admin" action. */
export function AdminEnterprises() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["enterprises"],
    queryFn: listEnterprises,
  });

  return (
    <div className="space-y-6">
      <PageTitle
        eyebrow="Admin"
        title="Enterprises"
        subtitle="Create tenants and invite their first admin."
      />

      <CreateEnterpriseForm onCreated={() => qc.invalidateQueries({ queryKey: ["enterprises"] })} />

      {q.isLoading && <Loading />}
      {q.error && <ErrorNote error={q.error} />}
      {q.data && (
        <div className="space-y-3">
          {q.data.enterprises.length === 0 && (
            <div className="text-sm text-gray-500">
              No enterprises yet — create one above.
            </div>
          )}
          {q.data.enterprises.map((e) => (
            <EnterpriseRow key={e.id} enterprise={e} />
          ))}
        </div>
      )}

      <ResetPasswordForm />
    </div>
  );
}

function CreateEnterpriseForm({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [timezone, setTimezone] = useState("Asia/Kolkata");

  const mut = useMutation({
    mutationFn: () =>
      createEnterprise({
        name: name.trim(),
        slug: slug.trim() || undefined,
        timezone: timezone.trim() || undefined,
      }),
    onSuccess: () => {
      setName("");
      setSlug("");
      onCreated();
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || mut.isPending) return;
    mut.mutate();
  };

  return (
    <Card title="Create enterprise">
      <form onSubmit={onSubmit} className="grid gap-3 sm:grid-cols-3">
        <div className="sm:col-span-1">
          <label className="block text-xs text-gray-500 mb-1">Name</label>
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="KLR Agro Ltd."
            className="w-full border rounded px-3 py-2 text-sm"
          />
        </div>
        <div className="sm:col-span-1">
          <label className="block text-xs text-gray-500 mb-1">
            Slug (optional)
          </label>
          <input
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="klr-agro"
            className="w-full border rounded px-3 py-2 text-sm font-mono"
          />
        </div>
        <div className="sm:col-span-1">
          <label className="block text-xs text-gray-500 mb-1">Timezone</label>
          <input
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className="w-full border rounded px-3 py-2 text-sm"
          />
        </div>
        <div className="sm:col-span-3">
          <button
            type="submit"
            disabled={mut.isPending}
            className="bg-agrilens-green text-white text-sm font-medium px-4 py-2 rounded hover:bg-agrilens-green-dark disabled:opacity-50"
          >
            {mut.isPending ? "Creating…" : "Create enterprise"}
          </button>
        </div>
      </form>
      {mut.isError && (
        <div className="mt-2">
          <ErrorNote error={mut.error} />
        </div>
      )}
    </Card>
  );
}

function EnterpriseRow({ enterprise }: { enterprise: Enterprise }) {
  const [email, setEmail] = useState("");
  const [result, setResult] = useState<InviteCreateResp | null>(null);

  const mut = useMutation({
    mutationFn: () => inviteEnterpriseAdmin(enterprise.id, email.trim()),
    onSuccess: (resp) => {
      setEmail("");
      setResult(resp);
    },
  });

  return (
    <div className="bg-white rounded shadow p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <Link
            to={`/admin/enterprise/${enterprise.id}`}
            className="text-lg font-semibold text-agrilens-green-dark hover:underline"
          >
            {enterprise.name}
          </Link>
          <div className="text-xs text-gray-500 font-mono">
            {enterprise.slug} · {enterprise.timezone}
          </div>
        </div>
        <Link
          to={`/admin/enterprise/${enterprise.id}`}
          className="text-sm text-agrilens-green hover:underline"
        >
          Manage states →
        </Link>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!email.trim() || mut.isPending) return;
          setResult(null);
          mut.mutate();
        }}
        className="mt-3 flex flex-wrap gap-2 items-end"
      >
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs text-gray-500 mb-1">
            Invite enterprise admin
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@customer.com"
            className="w-full border rounded px-3 py-2 text-sm"
          />
        </div>
        <button
          type="submit"
          disabled={mut.isPending}
          className="bg-agrilens-green text-white text-sm font-medium px-4 py-2 rounded hover:bg-agrilens-green-dark disabled:opacity-50"
        >
          {mut.isPending ? "Sending…" : "Invite admin"}
        </button>
      </form>
      {mut.isError && (
        <div className="mt-2 text-xs text-red-700 bg-red-50 rounded p-2">
          {(mut.error as Error).message}
        </div>
      )}
      {result && <InviteResult resp={result} />}
    </div>
  );
}
